/**
 * @file RouteConfig.tsx
 * @description Configuration des routes de l'application avec gestion des rôles, redirections et chargement paresseux.
 */

import { FC, Suspense, lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import RoleBasedRoute from './RoleBasedRoute';
import { useAuthStore } from '../store/auth/useAuthStore';
import CabinetLayout from './CabinetLayout';

const FecPage = lazy(() => import('../pages/FecData/FecPage'));
const NotAuthorizedPage = lazy(() => import('../pages/Auth/NotAuthorized'));
const AdminLayout = lazy(() => import('./AdminLayout'));
const GroupLayout = lazy(() => import('./GroupLayout'));
const FiscalSettingsPage = lazy(() => import('../pages/FiscalSettings/FiscalSettingsPage')); //Charge la page Ajouter entreprise
const PowensErrorPage = lazy(() => import('../pages/Finconnection/PowensErrorPage'));
const PowensSuccessPage = lazy(() => import('../pages/Finconnection/PowensSuccessPage'));
const PowensRelay = lazy(() => import('../pages/Finconnection/PowensRelay'));
const NotFoundPage = lazy(() => import('../pages/NotFound/NotFoundPage'));

// Chargement paresseux des composants
const PointsSuspensionPage = lazy(() => import('../pages/PointsSuspens/PointsSuspensPage'));
const QuestionnairePage = lazy(() => import('../pages/Questionnaire/QuestionnaireHoc'));
const FoldersPage = lazy(() => import('../pages/Company/CompanyListPage'));
const SearchCompanyPage = lazy(() => import('../pages/Company/SearchCompanyPage'));
const AuthPage = lazy(() => import('../pages/Auth/AuthPage'));
const AdminPage = lazy(() => import('../pages/Admin/AdminPage'));
const CreateCabinetPage = lazy(() => import('../pages/Admin/CreateCabinetPage'));
const ResetAccountsPage = lazy(() => import('../pages/Admin/ResetAccountsPage'));
const ParticipantsAssignmentPage = lazy(
  () => import('../pages/Company/ParticipantsAssignmentPage'),
); // Charge la page Assignations des intervenants
const PieceDetail = lazy(() => import('../components/EcirturesAndPieces/Pieces/PieceDetailPage'));
const ClientInviationPage = lazy(() => import('../pages/Company/ClientInvitationPage')); //Charge la page invitation client
const MainDashboard = lazy(() => import('../components/MainDashboard/MainDashboard')); //Charge la page Dashboard
const EditCompanyPage = lazy(() => import('../pages/Company/EditCompanyPage')); //Charge la page Ajouter entreprise
const AddCompanyPage = lazy(() => import('../pages/Company/AddCompanyPage'));
const AccountingSettingsPage = lazy(
  () => import('../pages/AccountingSettings/AccountingSettingsPage'), //Charge la page de parametrage comptabilite
);
const FolderPointSuspensPage = lazy(
  () => import('../pages/FolderPointSuspens/FolderPointSuspensPage'),
);
const ResetPasswordPage = lazy(() => import('../pages/ResetPassword/ResetPasswordPage'));
const ForgotPasswordEmailPage = lazy(
  () => import('../pages/ForgotPasswordEmail/ForgotPasswordEmailPage'),
);
const TokenVerification = lazy(() => import('../pages/ResetPassword/TokenVerification'));
const PiecesPage = lazy(() => import('../pages/Pieces/PiecesPage'));
const JounalPage = lazy(() => import('../pages/Journal/JournalPage'));
const balancePage = lazy(() => import('../pages/Balance/BalancePage'));
const AnalytiquePage = lazy(() => import('../pages/AnalytiqueParameters/AnalytiqueParametersPage'));
const CcaPcaPage = lazy(() => import('../pages/CcaPca/CcaPcaPage'));
const CutOffPage = lazy(() => import('../pages/CutOff/CutOffPage'));
const GrandLivrePage = lazy(() => import('../pages/GrandLivre/GrandLivrePage'));
const LettragesPartielsPage = lazy(
  () => import('../pages/LettragesPartiels/LettragesPartielsPage'),
);
const EmpruntsPage = lazy(() => import('../pages/Emprunts/EmpruntsPage'));
const ImmobilisationsPage = lazy(() => import('../pages/Immobilisations/ImmobilisationsPage'));
const ImmobilisationsEtatComparativePage = lazy(
  () => import('../pages/Immobilisations/ImmobilisationsEtatPage'),
);
const FinconnectionPage = lazy(() => import('../pages/Finconnection/FinconnectionPage'));
const PowensTransactionsPage = lazy(
  () => import('../pages/FinconnectTransactions/FinconnectTransactionsPage'),
);
const PowensUsersAdminPage = lazy(() => import('../pages/PowensUsersAdmin/PowensUsersAdminPage'));
const UpdateAccountsAdminPage = lazy(
  () => import('../pages/UpdateAccountsAdmin/UpdateAccountsAdminPage'),
);
const CabinetParametersPage = lazy(
  () => import('../pages/CabinetParameters/CabinetParametersPage'),
);
const CcaInterestRatesAdminPage = lazy(() => import('../pages/Admin/CcaInterestRatesAdminPage'));
const CompteCourantsPage = lazy(() => import('../pages/CompteCourants/CompteCourantsPage'));

// Interface définissant la structure des routes
export interface AppRoute {
  path: string;
  component?: React.LazyExoticComponent<FC<any>> | FC<any>;
  requiredRole?: string[];
  children?: AppRoute[];
}

// Définition des routes avec les composants correspondants
const routes: AppRoute[] = [
  {
    path: '/auth',
    component: AuthPage,
  },
  {
    path: '/not-authorized',
    component: NotAuthorizedPage,
  },
  { path: '/validation', component: TokenVerification },
  {
    path: '/reset-password',
    component: ResetPasswordPage,
  },
  {
    path: '/forgot-password',
    component: ForgotPasswordEmailPage,
  },
  {
    path: 'conclusions',
    component: GroupLayout,
    children: [
      { path: 'powens/success', component: PowensSuccessPage },
      { path: 'powens/error', component: PowensErrorPage },
      { path: 'powens/relay', component: PowensRelay },
    ],
  },
  {
    path: '/cabinet',
    component: CabinetLayout,
    requiredRole: ['ROLE_COLLABORATEUR_CABINET', 'ROLE_ADMIN_CABINET', 'ROLE_CLIENT_CABINET'],
    children: [
      {
        path: '',
        component: FoldersPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET', 'ROLE_CLIENT_CABINET'],
      },
      {
        path: 'parametre-cabinet',
        component: CabinetParametersPage,
        requiredRole: ['ROLE_ADMIN_CABINET'],
      },
      {
        path: 'points-suspension',
        component: PointsSuspensionPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      { path: 'questionnaire', component: QuestionnairePage },
      {
        path: 'precompta',
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        component: GroupLayout,
        children: [
          { path: 'finconnect/banques', component: FinconnectionPage },
          {
            path: 'finconnect/transactions',
            component: PowensTransactionsPage,
          },
        ],
      },
      {
        path: 'test-fec',
        component: FecPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      {
        path: 'liste-des-dossiers',
        component: FoldersPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      {
        path: 'recherche',
        component: SearchCompanyPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      {
        path: 'entreprise',
        component: AddCompanyPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      {
        path: 'parametre-fiscal',
        component: FiscalSettingsPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      },
      {
        path: 'assignation-intervenants',
        component: ParticipantsAssignmentPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      }, // Route pour la page Assignations des intervenants (ajouter collaborateur)
      {
        path: 'invitation-client',
        component: ClientInviationPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      }, //Route pour la page invitation de client (ajouter client)
      { path: 'tableau-de-bord', component: MainDashboard }, //Route pour le tableau de bord principale
      {
        path: 'parametre-general',
        component: EditCompanyPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      }, //Route pour l'ajout d'entreprise

      {
        path: 'dossier-point-suspens',
        component: FolderPointSuspensPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
      }, //Route pour les points en suspens
      {
        path: 'parametre-comptabilite',
        component: GroupLayout,
        requiredRole: [
          'ROLE_COLLABORATEUR_CABINET',
          'ROLE_ADMIN_CABINET',
          'ROLE_ADMIN_APPLICATION',
        ],
        children: [
          { path: '', component: AccountingSettingsPage },
          { path: 'journaux', component: AccountingSettingsPage },
          { path: 'plan-comptable', component: AccountingSettingsPage },
          { path: 'lettrage-auto', component: AccountingSettingsPage },
          { path: 'politique-immobilisation', component: AccountingSettingsPage },
          { path: 'fiscal', component: FiscalSettingsPage },
          { path: 'analytique', component: AnalytiquePage },
        ],
      },
      {
        path: 'production-comptable',
        component: GroupLayout,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        children: [
          { path: '', component: PiecesPage },
          { path: 'pieces', component: PiecesPage },
          { path: 'journal', component: JounalPage },
          { path: 'journal/lettrage-partie', component: LettragesPartielsPage },
          { path: 'cca-pca/:id', component: CcaPcaPage },
          { path: 'balance', component: balancePage },
          {
            path: 'pieces/:id', // Dynamic route to view a specific piece detail by ID
            component: PieceDetail,
          },
          //Route pour la page grand livre
          {
            path: 'grand-livre',
            component: GrandLivrePage,
            requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
            children: [{ path: '', component: GrandLivrePage }],
          },
        ],
      },

      {
        path: 'cut-off',
        component: CutOffPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        children: [
          { path: '', component: CutOffPage },
          { path: 'cca', component: CutOffPage },
          { path: 'pca', component: CutOffPage },
          { path: 'fnp', component: CutOffPage },
          { path: 'aar', component: CutOffPage },
          { path: 'fae', component: CutOffPage },
          { path: 'aae', component: CutOffPage },
          { path: 'par', component: CutOffPage },
          { path: 'cap', component: CutOffPage },
        ],
      },
      {
        path: 'emprunts',
        component: EmpruntsPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        children: [{ path: '', component: EmpruntsPage }],
      },
      {
        path: 'comptes-courants',
        component: CompteCourantsPage,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        children: [{ path: '', component: CompteCourantsPage }],
      },
      {
        path: 'immobilisations',
        component: GroupLayout,
        requiredRole: ['ROLE_COLLABORATEUR_CABINET'],
        children: [
          { path: '', component: ImmobilisationsPage },
          { path: 'etat', component: ImmobilisationsEtatComparativePage },
        ],
      },
    ], //Route pour la page production de comptabilté ,
  },
  {
    path: '/admin',
    component: AdminLayout, // Utilisation de AdminLayout ici
    requiredRole: ['ROLE_ADMIN_APPLICATION'],
    children: [
      { path: '', component: AdminPage },
      { path: 'creer-cabinet', component: CreateCabinetPage },
      {
        path: 'reset-comptes',
        component: ResetAccountsPage, // Page avec le bouton "Réinitialiser"
      },
      {
        path: 'powens-users',
        component: PowensUsersAdminPage,
      },
      {
        path: 'comptes',
        component: UpdateAccountsAdminPage,
      },
      {
        path: 'taux_cca',
        component: CcaInterestRatesAdminPage,
      },
    ],
  },
];

const renderRoute = (route: any, index: number) => (
  <Route
    key={index}
    path={route.path}
    element={
      route.requiredRole ? <RoleBasedRoute route={route} /> : route.component && <route.component />
    }
  >
    {route.children?.map(renderRoute)}
  </Route>
);

const RouteConfig: FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <Suspense fallback={<div>Chargement...</div>}>
      <Routes>
        {/* Default redirect */}
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/cabinet' : '/auth'} replace />}
        />

        {routes.map(renderRoute)}

        {/* 404 Page and Catch-all */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

export default RouteConfig;
